import React, { useEffect, useMemo } from 'react';
import { Segment, Table } from 'tea-component';
import { reviewsFormat } from '@op/utils/value';
import { useLocalLanguage } from '@op/utils/languageValue';
import DboExpandCollapseText from '@op/components/ExpandCollapse';
import CardCommon from '@op/components/CardCommon';
import EventDetailPanel from './EventDetailPanel';
import useLanguage from '@op/hooks/useLanguage';
import { getBasePath } from '@/utils/utils';
import {
  IEventItem,
  TLanguageType,
  TSentimentType,
} from '../types';
import { LanguageType } from '@op/types/common';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon, { summaryHotContentNegativeText, summaryHotContentPositiveText } from '@op/components/BubbleCommon';
import { DBO_U } from '@op/utils/format';

const { radioable, scrollable } = Table.addons;

interface IEventContentProps {
  eventList: IEventItem[];
  selectedEvent: IEventItem | null;
  setSelectedEvent: (event: IEventItem | null) => void;
  listLoading: boolean;
  languageType: TLanguageType;
  gameInfo: Record<string, any>;
  sentimentType: TSentimentType;
  setSentimentType: (value: TSentimentType) => void;
  wholeGameInfo: Record<string, any>;
  /** API 返回的开始时间 */
  startTime: string;
  /** API 返回的结束时间 */
  endTime: string;
  /** 任务 ID */
  taskId: string;
  type?: string;
}

/** 主内容区：左侧事件列表 + 右侧详情面板 */
export default function EventContent({
  eventList,
  selectedEvent,
  setSelectedEvent,
  listLoading,
  languageType,
  gameInfo,
  sentimentType,
  setSentimentType,
  wholeGameInfo,
  startTime,
  endTime,
  taskId,
  type = 'advancedGptData'
}: IEventContentProps) {
  const { formatMessage } = useLanguage();
  const { language } = useLocalLanguage({ languageType });

  // 构建 filterParams 供子组件请求 stats + keyword
  const filterParams = useMemo(() => ({
    channel_type: 'total',
    top_n: 30,
    sentiment_type: sentimentType,
    days: 1,
    language: 'all',
    start_time: startTime,
    end_time: endTime,
    keyword_filter_types: [] as string[],
    task_id: taskId,
    ...gameInfo,
  }), [gameInfo, sentimentType, startTime, endTime, taskId]);

  // 当事件列表变化时，自动选中第一项
  useEffect(() => {
    if (eventList.length > 0 && !selectedEvent) {
      setSelectedEvent(eventList[0]);
    }
  }, [eventList]);

  // Segment 选项
  const segmentOptions = useMemo(() => {
    const list = [
      { text: formatMessage({ id: 'Total-2' }), value: 'total' },
      {
        text: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={summaryHotContentPositiveText}
          >
            <div className="dbo-inlineflex">
              <SvgIcon type="laugh-green-color" />
              {formatMessage({ id: 'Positive' })}
            </div>
          </BubbleCommon>
        ),
        value: 'positive',
      },
      {
        text: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={summaryHotContentNegativeText}
          >
            <div className="dbo-inlineflex">
              <SvgIcon type="laugh-red-color" />
              {formatMessage({ id: 'Negative' })}
            </div>
          </BubbleCommon>
        ),
        value: 'negative',
      },
    ];

    if (wholeGameInfo?.use_neutral) {
      list.push({
        text: (
          <div className="dbo-inlineflex">
            <SvgIcon type="laugh-grey-color" />
            {formatMessage({ id: 'Neutral' })}
          </div>
        ),
        value: 'neutral',
      });
    }

    return list;
  }, [wholeGameInfo, formatMessage]);


  // 下载处理
  const handleDownload = () => {
    if (eventList.length === 0) return;
    DBO_U.open(`/opinion/NewDownload?gameid=${gameInfo?.edition_unified_id}&task_id=${taskId}&type=${type}`)
  };

  return (
    <CardCommon
      className="dbo-eventdetails-card"
      loading={listLoading}
      loadingHeight={500}
      empty={eventList.length === 0}
      emptyHeight={500}
      hasDownload={!listLoading && eventList.length > 0}
      handleDownloadOnClick={handleDownload}
      title={(
        <Segment
          className="dbo-eventdetails-segment"
          value={sentimentType}
          onChange={(value) =>
            setSentimentType(value as TSentimentType)
          }
          options={segmentOptions}
        />
      )}
    >
      <div className="dbo-eventdetails-flex">
        <Table
          className="dbo-eventdetails-flex-table"
          records={eventList}
          bordered
          recordKey="id"
          verticalTop
          columns={[
            {
              key: 'instance',
              header: formatMessage({ id: 'Hot Content' }),
              render: (cvm: IEventItem) => (
                <EventListItem
                  cvm={cvm}
                  language={language as LanguageType}
                />
              ),
            },
            {
              key: 'num',
              header: formatMessage({ id: 'Mentions' }),
              align: 'right',
              width: 90,
              render: (cvm: IEventItem) => (
                <div className="table-flex-num">
                  {reviewsFormat(cvm.comment_count)}
                </div>
              ),
            },
          ]}
          addons={[
            radioable({
              value: selectedEvent?.id != null ? String(selectedEvent.id) : '',
              onChange: (_key: string, content: any) => {
                setSelectedEvent(content.record);
              },
              rowSelect: true,
            }),
            scrollable({
              maxHeight: 915,
            }),
          ]}
        />

        <EventDetailPanel
          eventId={selectedEvent?.id}
          filterParams={filterParams}
          languageType={languageType}
        />
      </div>
    </CardCommon>
  );
}

/** 事件列表行项 */
function EventListItem({
  cvm,
  language,
}: {
  cvm: IEventItem;
  language: LanguageType;
}) {
  const title = language === 'zh' ? (cvm.event_title_cn || cvm.event_title) : cvm.event_title;
  const content = language === 'zh' ? (cvm.event_content_cn || cvm.event_content) : cvm.event_content;

  return (
    <div>
      <div className="table-flex">
        <SvgIcon
          className="table-flex-icon"
          type={cvm.sentiment_type === 'positive' ? 'laugh-green-color' : cvm.sentiment_type === 'negative' ? 'laugh-red-color' : 'laugh-grey-color'}
        />
        <div className="table-flex-title">
          {title}
        </div>
      </div>
      <DboExpandCollapseText
        text={content}
        rowNum={3}
        className="table-flex-text"
      />
    </div>
  );
}
